<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div
            v-if="!editCurrentPanel"
            class="header-title"
          >
            Exact Match Rule Details
          </div>
          <div
            v-else
            class="header-title"
          >
            Edit Exact Match Rule Details
          </div>
        </template>
      </EsHeader>
      <div
        v-if="!editCurrentPanel && !loading && !loadingError"
        class="icons"
      >
        <button
          v-if="hasModule('SYS:SYS:RULEMGR:EDIT')"
          class="button button-link"
          :class="{ disabled: editOtherPanels }"
          @click="editExactMatchRuleDetails()"
          :disabled="editOtherPanels"
        >
          <font-awesome-icon :icon="['far', 'edit']" title="Edit" />
        </button>
        <button
          v-if="hasModule('SYS:SYS:RULEMGR:EDIT')"
          class="button button-link"
          @click="openDeleteModule()"
          :class="{ disabled: editOtherPanels }"
          :disabled="editOtherPanels"
          ref="deleteButtonRef"
        >
          <font-awesome-icon :icon="['far', 'trash-alt']" title="Delete Exact Match Rule" />
        </button>
        <button
          @click="goBackToExactMatchRules()"
          class="button button-link"
        >
          <font-awesome-icon
            :icon="['far', 'angle-double-left']"
            size="lg" title="View Details" />
        </button>
      </div>
    </div>
    <div class="content">
      <es-loader
        v-if="loading"
        style="margin: 5rem"
      />
      <es-exception
        v-else-if="loadingError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <div v-else>
        <table>
          <tbody>
            <tr>
              <th class="category">
                Rule Information
              </th>
            </tr>
            <tr>
              <td class="has-text-grey">
                Rule Name
              </td>
              <td class="text">
                <div class="control">
                  <input
                    class="input"
                    :class="{ 'is-static': !editCurrentPanel }"
                    :readonly="!editCurrentPanel"
                    type="text"
                    v-model="ruleDetails.ruleName"
                    :aria-label="ruleDetails.ruleName || 'Rule Name'"
                    :title="ruleDetails.ruleName"
                  >
                </div>
                <span
                  v-if="hasErrName"
                  class="help is-danger"
                >
                  {{ msgErrName }}
                </span>
              </td>
            </tr>
            <tr>
              <td
                class="has-text-grey"
                style="vertical-align: middle"
              >
                Status
              </td>
              <td class="text">
                <div class="control">
                  <div
                    class="buttons has-addons"
                    v-if="editCurrentPanel"
                  >
                    <button
                      v-for="(option, index) in enabledDisabled"
                      class="button button--form"
                      :class="{ 'is-active': ruleDetails.enable === option.code }"
                      :key="index"
                      @click="ruleDetails.enable = option.code"
                    >
                      {{ option.description }}
                    </button>
                  </div>
                  <input
                    v-else
                    class="input"
                    type="text"
                    :aria-label="ruleDetails.enable || 'Status'"
                    :class="{ 'is-static': !editCurrentPanel }"
                    :readonly="!editCurrentPanel"
                    :value="ruleDetails.enable === true ? 'Enabled' : 'Disabled'"
                  >
                </div>
              </td>
            </tr>
            <tr>
              <td class="has-text-grey">
                Person Type
              </td>
              <td class="text">
                <div class="control">
                  <es-loader v-if="sortedAvailablePersonTypes.length === 0"/>
                  <input
                    v-else
                    class="input"
                    type="text"
                    :aria-label="ruleDetails.personType || 'Person Type'"
                    :class="{ 'is-static': !editCurrentPanel }"
                    :disabled="editCurrentPanel"
                    :readonly="!editCurrentPanel"
                    :value="mappedPersonTypeName"
                  >
                </div>
              </td>
            </tr>
            <tr>
              <th class="category" colspan="2">
                Source(s)
              </th>
            </tr>
            <tr>
              <td class="no-border" colspan="2">
                <div
                  v-if="!editCurrentPanel"
                  class="tags"
                >
                  <strong
                    v-for="(eachSource, index) in ruleSources"
                    class="tag source-tag"
                    :key="index"
                  >
                    {{ eachSource }}
                  </strong>
                </div>
                <es-select
                  v-else
                  keyValue="code"
                  valueTpl="${description}"
                  :clearOut="true"
                  :errorMsg="msgErrSource"
                  :hasError="hasErrSource"
                  :multi="true"
                  :preSelected="preSelectedSources"
                  :setValues="activeSources"
                  @update:values="source = $event"
                />
              </td>
            </tr>
            <tr>
              <th class="category" colspan="2">
                Field(s)
              </th>
            </tr>
            <tr>
              <td class="no-border" colspan="2">
                <div
                  v-if="!editCurrentPanel"
                  class="tags"
                >
                  <strong
                    v-for="(eachField, index) in ruleDetails.matchFieldsList"
                    class="tag source-tag"
                    :key="index"
                  >
                    {{ mapFields(eachField) }}
                  </strong>
                </div>
                <es-select
                  v-else
                  keyValue="code"
                  valueTpl="${description}"
                  :clearOut="true"
                  :errorMsg="msgErrFields"
                  :hasError="hasErrFields"
                  :multi="true"
                  :preSelected="preSelectedFields"
                  :setValues="this.fieldOptions"
                  @update:values="fields = $event"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div
          v-if="editCurrentPanel"
          class="is-flex flex-justify--end flex-align--center"
          style="margin-right: 5.15rem; padding-bottom: 2rem"
        >
          <button
            class="button button-link has-underline"
            style="margin-right: 1rem"
            @click.prevent="cancelPanel()"
          >
            Cancel
          </button>
          <button
            class="button is-primary"
            @click.prevent="updateRule()"
          >
            Update
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showModule"
      class="modal is-active"
    >
      <div class="modal-background"/>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title has-text-weight-bold">
            Delete Exact Match Rule
          </p>
          <button
            aria-label="close"
            class="delete"
            @click="closeDeleteModule"
            ref="closeButtonRef"
          ></button>
        </header>
        <section class="modal-card-body">
          <div class="modal-body">
            You cannot recover deleted Exact Match Rule.<br/>
            Are you sure you want to delete this Exact Match Rule?
          </div>
          <div class="is-flex flex-justify--end flex-align--center">
            <button
              class="button button-link"
              style="position:relative; right:25px; vertical-align: middle"
              @click="closeDeleteModule"
            >
              Cancel
            </button>
            <button
              class="button is-primary margin-left"
              @click="deleteRule()"
            >
              Delete Exact Match Rule
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faEdit,
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleLeft,
  faEdit,
  faTrashAlt,
);

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
    EsSelect,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
    ruleId: {
      type: Number,
    },
    sortedAvailablePersonTypes: {
      type: Array,
    },
  },
  created() {
    this.enabledDisabled = [
      {
        code: true,
        description: 'Enabled',
      },
      {
        code: false,
        description: 'Disabled',
      },
    ];
    this.userPersonTypesCodes = this.userPersonTypes
      .map(item => item.code);
  },
  mounted() {
    this.onLoad();
  },
  data() {
    return {
      cachedRuleDetails: {},
      editCurrentPanel: false,
      fields: [],
      fieldOptions: [],
      hasErrFields: false,
      hasErrName: false,
      hasErrSource: false,
      loading: false,
      loadingError: false,
      mappedPersonType: null,
      mappedPersonTypeName: null,
      mappedPersonTypeCode: null,
      msgErrFields: null,
      msgErrName: null,
      msgErrSource: null,
      ruleDetails: {},
      ruleSources: {},
      showModule: false,
      source: [],
      userPersonTypesCodes: null,
    };
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters('lookups', [
      'activeSources',
      'defaultPersonType',
    ]),
    payLoad() {
      const arr = {};
      arr.id = this.ruleDetails.id;
      arr.personType = this.ruleDetails.personType;
      arr.ruleName = this.ruleDetails.ruleName;
      arr.sourcesJson = JSON.stringify({
        sources: this.source
          .filter(s => s && s.code)
          .map(s => s.code),
      });
      arr.enable = this.ruleDetails.enable === true ? 1 : 0;
      arr.matchFieldsJson = JSON.stringify({
        fields: this.fields
          .filter(f => f && f.code)
          .map(f => f.code),
      });
      return arr;
    },
    preSelectedFields() {
      const selectedFields = [];
      this.ruleDetails.matchFieldsList.forEach((item) => {
        selectedFields.push(this.fieldOptions.find(f => f.code === item));
      });
      return selectedFields;
    },
    preSelectedSources() {
      const selectedSources = [];
      this.ruleSources.forEach((item) => {
        const val = this.activeSources.find(f => f && f.code === item);
        if (val) selectedSources.push(val);
      });
      return selectedSources;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadSources',
    ]),
    cacheRule() {
      const arr = {};
      arr.personType = this.ruleDetails.personType;
      arr.ruleName = this.ruleDetails.ruleName;
      arr.sourcesJson = JSON.stringify({
        sources: this.source
          .filter(s => s && s.code)
          .map(s => s.code),
      });
      arr.enable = this.ruleDetails.enable === true ? 1 : 0;
      arr.matchFieldsJson = JSON.stringify({
        fields: this.fields
          .filter(f => f && f.code)
          .map(f => f.code),
      });
      this.cachedRuleDetails = JSON.parse(JSON.stringify(arr));
      return this.cachedRuleDetails;
    },
    cancelPanel() {
      this.hideNotification();
      this.editCurrentPanel = !this.editCurrentPanel;
      this.$emit('allowEditOtherPanels');
    },
    checkForFormChanges() {
      return this.compareObjects(this.cachedRuleDetails, this.payLoad);
    },
    checkRequiredFields() {
      const fields = [
        {
          condition: this.ruleName === null || this.ruleName === '',
          errorKey: 'hasErrName',
          errorMsgKey: 'msgErrName',
          errorMsg: 'Rule Name is required.',
        },
        {
          condition: !this.source.length,
          errorKey: 'hasErrSource',
          errorMsgKey: 'msgErrSource',
          errorMsg: 'Source is required.',
        },
        {
          condition: !this.fields.length,
          errorKey: 'hasErrFields',
          errorMsgKey: 'msgErrFields',
          errorMsg: 'Field is required.',
        },
      ];

      fields.forEach((field) => {
        this[field.errorKey] = field.condition;
        this[field.errorMsgKey] = field.condition ? field.errorMsg : null;
      });
    },
    closeDeleteModule() {
      this.showModule = false;
      this.$nextTick(() => {
        if (this.$refs.deleteButtonRef) {
          this.$refs.deleteButtonRef.focus(); // Focus on delete icon when modal closes
        }
      });
    },
    compare(a, b) {
      if (a.description.toUpperCase() < b.description.toUpperCase()) {
        return -1;
      }
      if (a.description.toUpperCase() > b.description.toUpperCase()) {
        return 1;
      }
      return 0;
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    async deleteRule() {
      this.loading = true;
      this.loadingError = false;
      try {
        await axios.delete(`rule-manager/person-id/exact-match-rules/${this.ruleId}`);
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
        this.showModule = false;
        this.$emit('toggleExactMatchRules');
        this.showNotification('success', ['The Exact Match Rule has been deleted.']);
      }
    },
    editExactMatchRuleDetails() {
      this.editCurrentPanel = true;
      this.$emit('allowEditOtherPanels');
    },
    goBackToExactMatchRules() {
      this.$emit('toggleExactMatchRules');
    },
    mapFields(code) {
      return this.fieldOptions.find(f => f.code === code).description;
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        await this.loadSources();
        const { data } = await axios.get(`rule-manager/person-id/exact-match-rules/${this.ruleId}`);
        this.ruleDetails = data;
        const { personType, sourcesJson } = this.ruleDetails;
        const mappedPersonType = this.sortedAvailablePersonTypes.find(f => f.code === personType);
        if (mappedPersonType) {
          const { name, code } = mappedPersonType;
          this.mappedPersonType = mappedPersonType;
          this.mappedPersonTypeName = name;
          this.mappedPersonTypeCode = code;
          const fields = [
            'FIRST_NAME', 'LAST_NAME', 'MIDDLE_NAME', 'SUFFIX', 'PREVIOUS_LAST_NAME',
            'DATE_OF_BIRTH', 'GENDER_CODE', 'RACE_ETHNIC_CODE', 'ETHNICITY_INDICATOR',
            'DISTRICT_CODE', 'SCHOOL_CODE', 'RES_DISTRICT_CODE', 'LOCAL_STUDENT_ID',
            'SOURCE_CODE', 'GRADE_CODE', 'SCHOOL_YEAR', 'SOCIAL_SECURITY_NUMBER',
          ];
          this.fieldOptions = fields.map(field => ({
            code: field,
            description: this.getFieldLabel(`PER:${code}:ASSIGN:${field}`),
          })).sort(this.compare);
          this.ruleSources = JSON.parse(sourcesJson).sources;
          this.source = this.preSelectedSources;
          this.fields = this.preSelectedFields;
          this.cacheRule();
        } else {
          throw new Error('Mapped person type not found.');
        }
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    openDeleteModule() {
      this.showModule = true;
      this.$nextTick(() => {
        if (this.$refs.closeButtonRef) {
          this.$refs.closeButtonRef.focus(); // Focus on close icon when modal opens
        }
      });
    },
    async updateRule() {
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.cancelPanel();
        return;
      }
      this.checkRequiredFields();
      if (this.hasErrName || this.hasErrSource || this.hasErrFields) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        return;
      }
      try {
        this.loading = true;
        this.loadingError = false;
        await axios({
          method: 'put',
          url: `/rule-manager/person-id/exact-match-rules/${this.ruleId}`,
          data: this.payLoad,
        });
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
        this.$emit('allowEditOtherPanels');
        this.$emit('toggleExactMatchRules');
        this.showNotification('success', ['The Exact Match Rule has been updated successfully.']);
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table td
    font-size: 12px
    vertical-align: middle
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  .category
    padding-top: 1.5rem
    border: 0
  .content
    padding: 0 3rem 0.5rem
  .header
    display: flex
    align-items: center
  .icons
    width: auto
    display: flex
    justify-content: space-around
    padding-right: 1rem
  .modal-body
    margin-bottom: 1rem
    display: flex
    text-align: left
    justify-content: flex-start
  .modal-card-head
    padding: 8px 16px
  .modal-card-title
    display: flex
    font-size: 15.2px
  .no-border
    border: 0
  .text
    width: 100%
    font-size: 14px
    text-align: right
    display: flex
    justify-content: flex-end
    align-items: center
  .control
    display: inline-block
    text-align: right
  .input.is-static
    cursor: pointer
    overflow: hidden
    text-overflow: ellipsis
    text-wrap: nowrap
</style>
